/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

/* Import configuration */
import { GENERAL_STATUS_CRITICAL, GENERAL_STATUS_WARNING } from "@/config/app";

export const OVERVIEW_STATUS_CATEGORIES = ['danger', 'warning', 'success'] as const;
export const STATUS_SEVERITIES = ['acknowledged', 'error', 'inactive', 'implausible', 'ok', 'warning'] as const;
export const TRAFFIC_LIGHT_SEVERITIES = ['danger', 'implausible', 'success', 'warning'] as const;
export const OVERVIEW_METADATA_SORT_KEYS = ['anomaly', 'customer', 'sensor', 'system'] as const;

export type OverviewSortDirection = 'asc' | 'desc';
export type OverviewStatusCategory = typeof OVERVIEW_STATUS_CATEGORIES[number];
export type StatusSeverity = typeof STATUS_SEVERITIES[number];
export type TrafficLightSeverity = typeof TRAFFIC_LIGHT_SEVERITIES[number];
export type OverviewStatusSeverity = Extract<StatusSeverity, 'error' | 'implausible' | 'ok' | 'warning'>;
export type OverviewMetadataSortKey = typeof OVERVIEW_METADATA_SORT_KEYS[number];

export interface OverviewSortOption {
    by: number | OverviewMetadataSortKey;
    direction: OverviewSortDirection;
}

export interface OverviewCardSortData {
    anomaly: string;
    customer: string;
    sensor: string;
    system: string;
}

/** Customer sorting is not meaningful when the overview contains one customer at most. */
export function getAvailableOverviewMetadataSortKeys(
    customerCount: number,
): OverviewMetadataSortKey[] {
    return customerCount > 1
        ? [...OVERVIEW_METADATA_SORT_KEYS]
        : OVERVIEW_METADATA_SORT_KEYS.filter((sortKey) => sortKey !== 'customer');
}

export interface OverviewStatusCounts {
    danger: number;
    success: number;
    warning: number;
}

export function formatOverviewSortLabel(sensorType: string): string {
    return sensorType
        .split('_')
        .filter(Boolean)
        .map((part) => {
            return part.charAt(0).toUpperCase() + part.slice(1);
        })
        .join(' ');
}

export function parseOverviewDefaultSort(
    defaultSortValue: string,
): { direction: OverviewSortDirection; sensorType: string } | null {
    if (!defaultSortValue) {
        return null;
    }

    const [direction, ...sensorTypeParts] = defaultSortValue.split('|');
    const sensorType = sensorTypeParts.join('|');

    if ((direction !== 'asc' && direction !== 'desc') || !sensorType) {
        return null;
    }

    return {
        direction,
        sensorType,
    };
}

export function createOverviewSortOption(
    direction: OverviewSortDirection,
    sensorType: string,
    sortKeys: string[],
): OverviewSortOption | null {
    if (!sensorType) {
        return null;
    }

    if (OVERVIEW_METADATA_SORT_KEYS.includes(sensorType as OverviewMetadataSortKey)) {
        return {
            by: sensorType as OverviewMetadataSortKey,
            direction,
        };
    }

    const by = sortKeys.findIndex((currentKey) => currentKey === sensorType);

    if (by < 0) {
        return null;
    }

    return {
        by,
        direction,
    };
}

export function sortOverviewCardItems<T extends OverviewCardSortData>(
    items: T[],
    option: OverviewSortOption,
): T[] {
    const metadataSortKey = option.by;

    if (typeof metadataSortKey !== 'string') {
        return items;
    }

    const directionFactor = option.direction === 'desc' ? -1 : 1;

    return [...items].sort((leftItem, rightItem) => {
        return leftItem[metadataSortKey].localeCompare(rightItem[metadataSortKey]) * directionFactor;
    });
}

export function getOverviewAnomalyBadgeClassName(anomaly: string | null): string {
    switch (anomaly) {
        case 'pv':
            return 'badge badge-success';
        case 'bl':
            return 'badge badge-primary';
        case 'bl_night':
            return 'badge badge-secondary';
        case 'heating_energy':
            return 'badge badge-info';
        default:
            return 'badge badge-accent';
    }
}

export function getOverviewKpiSurfaceClassName(anomaly: string | null): string {
    switch (anomaly) {
        case 'pv':
            return 'bg-success/10';
        case 'bl':
        case 'bl_night':
            return 'bg-primary/10';
        case 'heating_energy':
            return 'bg-info/10';
        default:
            return 'bg-base-100/70';
    }
}

export function getOverviewKpiAggregationLabel(
    aggregation: 'average' | 'max' | 'sum',
): string {
    switch (aggregation) {
        case 'average':
            return 'Average';
        case 'max':
            return 'Maximum';
        case 'sum':
            return 'Sum';
    }
}

export function getOverviewStatusClass(status: number): string {
    return getStatusClassName(getOverviewStatusSeverity(status));
}

export function getStatusClassName(severity: StatusSeverity): string {
    return `status--${severity}`;
}

export function getStatusSeverityFromTrafficLight(
    severity: TrafficLightSeverity,
): StatusSeverity {
    switch (severity) {
        case 'danger':
            return 'error';
        case 'success':
            return 'ok';
        case 'implausible':
            return 'implausible';
        case 'warning':
        default:
            return 'warning';
    }
}

export function getOverviewStatusSeverity(status: number): OverviewStatusSeverity {
    if (status < 0) {
        return 'implausible';
    }

    switch (getOverviewStatusCategory(status)) {
        case 'danger':
            return 'error';
        case 'warning':
            return 'warning';
        default:
            return 'ok';
    }
}

export function getOverviewStatusCategory(
    status: number,
): OverviewStatusCategory {
    if (status >= GENERAL_STATUS_CRITICAL) {
        return 'danger';
    }

    if (status >= GENERAL_STATUS_WARNING) {
        return 'warning';
    }

    return 'success';
}
